import React, { useState, useEffect } from 'react';
import { FiCheck, FiX, FiMessageSquare, FiMail, FiUser, FiCalendar, FiSend } from 'react-icons/fi';
import { getComments, approveComment, deleteComment, replyToComment } from '../services/blog';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import ReplyModal from '../components/Blog/ReplyModal';
import toast from 'react-hot-toast';

export default function BlogComments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);

  useEffect(() => {
    fetchComments();
  }, [filters]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const data = await getComments(filters);
      setComments(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch comments');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (commentId) => {
    try {
      await approveComment(commentId);
      toast.success('Comment approved');
      fetchComments();
    } catch (error) {
      toast.error('Failed to approve comment');
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await deleteComment(commentId);
      toast.success('Comment deleted');
      fetchComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleReply = async (commentId, reply) => {
    try {
      await replyToComment(commentId, reply);
      toast.success('Reply added');
      setReplyModalOpen(false);
      fetchComments();
    } catch (error) {
      toast.error('Failed to add reply');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Blog Comments</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Comments Table */}
      {loading ? (
        <LoadingSpinner />
      ) : comments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500">
          No comments found
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commenter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Post</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {comments.map((comment) => (
                  <tr key={comment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800 flex items-center gap-1">
                          <FiUser className="text-gray-400" /> {comment.name}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <FiMail className="text-gray-400" /> {comment.email || 'No email'}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={`/blog/${comment.post_slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-500 hover:text-primary-600 text-sm"
                      >
                        {comment.post_title}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-600 text-sm max-w-md">{comment.comment}</p>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatDate(comment.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      {comment.is_approved ? (
                        <span className="text-green-600 text-sm flex items-center gap-1">
                          <FiCheck /> Approved
                        </span>
                      ) : (
                        <span className="text-yellow-600 text-sm flex items-center gap-1">
                          <FiX /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!comment.is_approved && (
                        <button
                          onClick={() => handleApprove(comment.id)}
                          className="text-green-500 hover:text-green-700 mr-3"
                          title="Approve"
                        >
                          <FiCheck className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedComment(comment);
                          setReplyModalOpen(true);
                        }}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                        title="Reply"
                      >
                        <FiSend className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(comment.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Reply Modal */}
      <ReplyModal
        isOpen={replyModalOpen}
        onClose={() => {
          setReplyModalOpen(false);
          setSelectedComment(null);
        }}
        onReply={(reply) => handleReply(selectedComment?.id, reply)}
        comment={selectedComment}
      />
    </div>
  );
}
